import querystring from "querystring";
import {
  includes,
  isEmpty,
  join,
  map,
  mergeAll,
  mergeWith,
  modify,
  omit,
  pipe,
  pluck,
  zipObj,
} from "ramda";
import { API_BASE_URL } from "../props";
import { combineStrings, notBlank } from "../util/ext";
import { useGet } from "../common/fetchServices";
import { useSelector } from "react-redux";

export const LOCAL_STORAGE_CONTENT_SEARCH_PREFERENCES_KEY =
  "biblioboard-management-ui-content-search-preferences";

export const MEDIA_SEARCHLIMIT = 10;
const MEDIA_ID_REGEX = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;

const getTermExpressions = map(facet =>
  pipe(
    pluck("code"),
    map(code =>
      includes(facet.queryParam, ["_pd", "status"]) ? code : `"${code}"`,
    ),
    join(" OR "),
  )(facet.terms),
);

const getYearGroupExpression = pipe(pluck("expression"), join(" OR "));

const getFacetTermCodeExpressions = facets =>
  zipObj(pluck("queryParam", facets), getTermExpressions(facets));

const mergeQueryExpressions = mergeWith(combineStrings(" OR "));

export const buildSearchState = searchState => {
  const {
    appliedQuery,
    appliedFacets,
    appliedPublicationYearGroups,
    orderBy,
    orderDirection,
    page,
    titleOnly,
    moduleOnly,
    limit,
    facetsOnly,
    noFacets,
    otherParams,
  } = searchState;
  return {
    query: notBlank(appliedQuery) ? appliedQuery : [],
    facets: notBlank(appliedFacets) ? appliedFacets : [],
    publicationYearGroups: notBlank(appliedPublicationYearGroups)
      ? appliedPublicationYearGroups
      : [],
    orderBy: notBlank(orderBy) ? `${orderBy} ${orderDirection}` : null,
    page: notBlank(page) ? page : 0,
    titleOnly: notBlank(titleOnly) ? titleOnly : false,
    moduleOnly: notBlank(moduleOnly) ? moduleOnly : false,
    limit: notBlank(limit) ? limit : MEDIA_SEARCHLIMIT,
    facetsOnly: notBlank(facetsOnly) ? facetsOnly : false,
    noFacets: noFacets ? noFacets : false,
    otherParams: notBlank(otherParams) ? otherParams : [],
  };
};

const getSearchParameters = ({
  query,
  facets,
  publicationYearGroups,
  orderBy,
  offset,
  limit,
  titleOnly,
  moduleOnly,
  otherParams,
  facetsOnly,
  noFacets,
}) => {
  const termCodesQueryExpressions = getFacetTermCodeExpressions(facets);
  const yearGroupQueryExpression = moduleOnly
    ? {}
    : !isEmpty(publicationYearGroups)
    ? { _pd: getYearGroupExpression(publicationYearGroups) }
    : {};

  const facetQueryExpressions = mergeQueryExpressions(
    yearGroupQueryExpression,
    termCodesQueryExpressions,
  );

  const queryText = isEmpty(query) ? "*" : query;

  const sortFields = notBlank(orderBy) ? { "sort-fields": orderBy } : {};

  const facetsOnlyParam = facetsOnly ? { "facets-only": true } : {};

  const facetListParam = noFacets ? { "facet-list": "[]" } : {};

  const mediaIdQuery = MEDIA_ID_REGEX.test(queryText);

  return mergeAll([
    mediaIdQuery
      ? { id: queryText }
      : titleOnly
      ? { t: queryText }
      : moduleOnly
      ? { _mod: queryText }
      : { g: queryText },
    facetQueryExpressions,
    sortFields,
    { offset },
    { limit },
    facetsOnlyParam,
    facetListParam,
    ...otherParams,
  ]);
};

const getSearchUrl = (initialSearchState, useAdminRoute) => {
  const searchState = modify("query", join(" AND "), initialSearchState);
  const params = getSearchParameters(searchState);

  // Admin search forces owner scope by org-id
  // As we expand Creator to include "shared" content and content groups,
  // we should use /search/v2 which allows us to scope by creatorOrgs instead --
  // this will grab all items an org has access to as defined by the MAC table

  if (useAdminRoute) {
    return `${API_BASE_URL}/admin/search?${querystring.encode(params)}`;
  } else {
    return `${API_BASE_URL}/search/v2?${querystring.encode(params)}`;
  }
};

export const useSearch = (initialSearchState, useAdminRoute = true) => {
  const searchState = buildSearchState(initialSearchState);
  const auth = useSelector(state => state.auth);
  const { page, limit } = searchState;
  searchState.offset = page * limit;
  const searchUrl = getSearchUrl(omit(["page"], searchState), useAdminRoute);

  const orgId = auth.currentOrganization
    ? auth.currentOrganization.id
    : "admin";

  return useGet(["search", orgId, { url: searchUrl }], searchUrl);
};

export const useImageBookViewerSearch = (id, keyword) => {
  const url = `${API_BASE_URL}/search/${id}?keyword=${keyword}`;
  return useGet(["image-book-viewer-search", { url }], url);
};

export const useSolrLoaderQueueSize = () => {
  const url = `${API_BASE_URL}/admin/search/queue-size`;
  return useGet(["solr-loader-queue-size"], url);
};
